import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatPhoneNumber } from '@supply-mover-2-0/core';
import { PORTAL_SMS_DISCLOSURE } from '@supply-mover-2-0/shared';
import { Toast, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { answerBackInStockTextPrompt } from '../services/portalNotificationService';
import { useBackInStockTextPromptStore } from '../stores/backInStockTextPromptStore';
import { usePortalAuthStore } from '../stores/portalAuthStore';
import { PORTAL_NOTIFICATION_SETTINGS_PATH } from './PortalNotificationBell';

/**
 * PD-14306 — the one-time question shown after a customer's first "Notify
 * me": do they also want a text when the item is back?
 *
 * Back-in-stock texts need the customer's recorded consent, and a customer
 * who has never ticked a Text box would otherwise never be offered one. The
 * question carries the same disclosure every Text box does.
 *
 * - Yes records consent for the phone shown and turns Text on.
 * - No is remembered, and the question is never asked again.
 * - Closing it (the X, the backdrop, Escape) stores nothing, so the question
 *   comes back with the next "Notify me". Only a deliberate No ends it.
 *
 * The request itself is already saved by the time this shows; whatever the
 * answer, the customer is still notified in the storefront and by email as
 * their settings allow.
 *
 * Same shell as GetHelpModal (bottom sheet on phones, centered dialog on
 * md+). Mounted once by the layout and opened through
 * `useBackInStockTextPromptStore`.
 */
export function BackInStockTextPrompt(): JSX.Element | null {
	const { t } = useTranslation();
	const prompt = useBackInStockTextPromptStore((s) => s.prompt);
	const close = useBackInStockTextPromptStore((s) => s.close);
	const [isSaving, setIsSaving] = useState(false);

	const dismiss = useCallback((): void => {
		if (!isSaving) {
			close();
		}
	}, [isSaving, close]);

	// The layout unmounts this on sign-out. Drop any open question with it, so
	// it is not waiting for the next person who signs in on this browser.
	useEffect(
		() => (): void => {
			close();
		},
		[close],
	);

	useEffect(() => {
		if (prompt == null) {
			return undefined;
		}
		const onKey = (e: KeyboardEvent): void => {
			if (e.key === 'Escape') {
				dismiss();
			}
		};
		document.addEventListener('keydown', onKey);
		return (): void => {
			document.removeEventListener('keydown', onKey);
		};
	}, [prompt, dismiss]);

	const answer = async (acceptsTexts: boolean): Promise<void> => {
		setIsSaving(true);
		try {
			const saved = await answerBackInStockTextPrompt(
				usePortalAuthStore.getState().token,
				{ acceptsTexts },
			);

			if (acceptsTexts) {
				Toast.show(
					saved.textsOn
						? {
								type: 'success',
								text1: t('We will text you when it is back in stock'),
							}
						: {
								type: 'info',
								text1: t('Texts could not be turned on'),
								text2: t('You can manage texts in your notification settings.'),
							},
				);
			}
			close();
		} catch {
			// Nothing was stored, so leave the question open to answer again.
			Toast.show({
				type: 'error',
				text1: t('Your answer could not be saved'),
				text2: t('Please try again.'),
			});
		} finally {
			setIsSaving(false);
		}
	};

	if (prompt == null) {
		return null;
	}

	return (
		<>
			<button
				type="button"
				aria-label={t('Close')}
				onClick={dismiss}
				className="fixed inset-0 z-40 bg-black/40 drawer-backdrop-in"
			/>
			<div
				className="fixed inset-x-0 bottom-[max(calc(36px_+_env(safe-area-inset-bottom)),48px)] z-40 rounded-t-2xl bg-brand-surface shadow-2xl flex flex-col overflow-hidden filter-tray-in max-h-[80vh] md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-md md:rounded-2xl md:border md:border-brand-border"
				role="dialog"
				aria-modal="true"
				aria-label={t('Also get a text?')}
			>
				<div className="flex items-center justify-between px-4 h-12 border-b border-brand-border shrink-0">
					<h2 className="text-base font-semibold text-default">
						{t('Also get a text?')}
					</h2>
					<button
						type="button"
						onClick={dismiss}
						disabled={isSaving}
						aria-label={t('Close')}
						className="p-2 -mr-2 text-brand-muted hover:text-default min-h-touch disabled:opacity-60"
					>
						<FontAwesomeIcon icon={faTimes} />
					</button>
				</div>
				<div className="overflow-y-auto p-4">
					<p className="text-sm text-default">
						{t(
							'We will let you know when this item is back in stock. Would you also like a text at {phone}?',
						).replace('{phone}', formatPhoneNumber(prompt.phone))}
					</p>
					<p className="mt-2 text-xs text-brand-muted">
						{t(PORTAL_SMS_DISCLOSURE)}
					</p>
					<div className="mt-4 flex flex-col-reverse gap-2 md:flex-row md:justify-end">
						<button
							type="button"
							onClick={() => {
								void answer(false);
							}}
							disabled={isSaving}
							className="inline-flex items-center justify-center px-4 py-2 text-sm text-strong border border-brand-border rounded-lg hover:bg-brand-input min-h-touch disabled:opacity-60"
						>
							{t('No thanks')}
						</button>
						<button
							type="button"
							onClick={() => {
								void answer(true);
							}}
							disabled={isSaving}
							className="inline-flex items-center justify-center rounded-lg bg-portal-accent hover:bg-portal-accent-hover text-white text-sm font-medium px-4 py-2 transition-colors min-h-touch disabled:opacity-60"
						>
							{t('Yes, text me')}
						</button>
					</div>
					<p className="mt-3 text-xs text-brand-muted">
						{t('You can change this at any time in your')}{' '}
						<Link
							to={PORTAL_NOTIFICATION_SETTINGS_PATH}
							onClick={dismiss}
							className="text-portal-accent hover:underline"
						>
							{t('notification settings')}
						</Link>
						{t('.')}
					</p>
				</div>
			</div>
		</>
	);
}
